import type { FunctionComponent, SVGProps } from 'react';
import type { TProductName } from 'themes/tokens/productTheme';

import ActivityInsights from 'assets/icons/common/activityInsightsIcon.svg?react';
import BabyCryDetection from 'assets/icons/common/babyCryDetectionIcon.svg?react';
import BabyPlanListHeader from 'assets/icons/common/babyPlanListHeader.svg?react';
import BabyTempHumidity from 'assets/icons/common/babyTempHumidityIcon.svg?react';
import BirdDetected from 'assets/icons/common/birdDetectedIcon.svg?react';
import BirdPlanListHeader from 'assets/icons/common/birdPlanListHeader.svg?react';
import Data from 'assets/icons/common/dataIcon.svg?react';
import DescriptiveAIAlerts from 'assets/icons/common/descriptiveAIAlertsIcon.svg?react';
import EventStorage from 'assets/icons/common/eventStorageIcon.svg?react';
import FeatureUnavailable from 'assets/icons/common/featureUnavailableIcon.svg?react';
import HigherDataRates from 'assets/icons/common/higherDataRatesIcon.svg?react';
import HomeSecurityPlanListHeader from 'assets/icons/common/homePlanListHeader.svg?react';
import Hub from 'assets/icons/common/hubIcon.svg?react';
import Library from 'assets/icons/common/libraryIcon.svg?react';
import LimitedSecurity from 'assets/icons/common/limitedSecurityIcon.svg?react';
import LiveView from 'assets/icons/common/liveViewIcon.svg?react';
import MaxLimitIcon from 'assets/icons/common/maxLimitIcon.svg?react';
import MotionAlerts from 'assets/icons/common/motionAlertsIcon.svg?react';
import MultiPetRecognition from 'assets/icons/common/multiPetRecognitionIcon.svg?react';
import NoAlertsIcon from 'assets/icons/common/noAlerts.svg?react';
import NoDesktopAccess from 'assets/icons/common/noDesktopAccessIcon.svg?react';
import NoEventVideos from 'assets/icons/common/noEventVideosIcon.svg?react';
import PetPlanListHeader from 'assets/icons/common/petPlanListHeader.svg?react';
import PlanTierOne from 'assets/icons/common/planTierOne.svg?react';
import PlanTierThree from 'assets/icons/common/planTierThree.svg?react';
import PlanTierTwo from 'assets/icons/common/planTierTwo.svg?react';
import ProfessionalMonitoring from 'assets/icons/common/professionalMonitoringIcon.svg?react';
import SampleCreditCard from 'assets/icons/common/sampleCreditCard.svg?react';
import Snapshot from 'assets/icons/common/snapshotIcon.svg?react';
import Success from 'assets/icons/common/success.svg?react';
import TrailPlanListHeader from 'assets/icons/common/trailPlanListHeader.svg?react';
import UpgradeIcon from 'assets/icons/common/upgradeIcon.svg?react';
import Warning from 'assets/icons/common/warning.svg?react';
import Wiki from 'assets/icons/common/wikiIcon.svg?react';
import WildlifeIdentification from 'assets/icons/common/wildlifeIdentificationIcon.svg?react';
import { FEATURE_ICONS } from './featureIcons';

/** An SVG compiled to a component by vite-plugin-svgr (`?react`). */
export type TIconComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

/**
 * Icons every product and every partner shares.
 *
 * Add one by dropping the file in `src/assets/icons/common/` and adding a line:
 *
 *   import Eye from 'assets/icons/common/eye.svg?react';
 *   const COMMON_ICONS = { eye: Eye };
 *
 * A name only needs an entry in PRODUCT_ICONS if some product *replaces* it —
 * the common set stays the default and an override is opt-in, per icon, per
 * product.
 *
 * SCOPE — two kinds of art deliberately do NOT belong here and stay on B2 as
 * URLs:
 *
 *  - illustrations. Inlining costs bundle for art that is never recoloured;
 *    `noPlanComparision.svg` alone is 143 KB. Rule of thumb: over ~8 KB, leave
 *    it a URL.
 *  - anything whose own colours are the point — card-brand marks above all.
 *    An icon that must never be recoloured gains nothing from being inlined,
 *    so it stays an `<img src={partnerAssets.visaCard} />`. Everything in this
 *    registry is recolourable; that is the invariant, not a per-icon flag.
 *  - anything that varies by PARTNER. This registry is keyed by PRODUCT
 *    (home/bird/pet/baby/trail, from the route) and has no partner axis, while
 *    the B2 maps are keyed by partner (`VITE_PARTNER`). Icons such as
 *    `basicAlertsIcon` and `30DaysEventIcon` are genuinely different
 *    multi-colour artwork per partner, so one file here could not serve every
 *    build. Adding a partner axis is possible — `VITE_PARTNER` is build-time,
 *    so Vite would tree-shake the rest — but nothing needs it yet.
 */
const COMMON_ICONS = {
  // Plan tier badges. One drawing per tier, single-ink — every partner shipped
  // the identical path in its own brand colour, so recolouring at runtime
  // replaces twelve near-duplicate files with three, and gets per-PRODUCT
  // colour (pet, bird, trail) that the per-partner copies never had.
  planTierOne: PlanTierOne,
  planTierTwo: PlanTierTwo,
  planTierThree: PlanTierThree,

  // "Why skip the trial" benefit icons — duotone: the glyph is drawn in the
  // brand ink and a coral slash strikes it through to read as "you lose this".
  // Both inks follow the theme (see INK_ACCENT in Icon.tsx); the white halo
  // around the slash is structural and stays white. These replace five
  // per-partner B2 keys that were the same drawing in each partner's colours.
  noAlerts: NoAlertsIcon,
  higherDataRates: HigherDataRates,
  limitedSecurity: LimitedSecurity,
  noDesktopAccess: NoDesktopAccess,
  noEventVideos: NoEventVideos,

  // The premium upsell's benefit rows. Single-ink, so `color` alone drives
  // them.
  //
  // `descriptiveAIAlerts` was 42 KB as exported: Figma drew one shape four
  // times over (a fill, a 1px strokeAlign:INSIDE copy, a 0.3px copy, plus the
  // mask INSIDE forces it to emit) and wrote every coordinate to five decimal
  // places on a 24-unit canvas. Collapsing the four to a single fill + 0.3px
  // centred stroke — the only copy that changes the silhouette — and rounding
  // to two decimals gives 10 KB / 3.8 KB gzipped with zero pixels differing by
  // >10% at the 20px size it renders at. If design re-exports it, redo this or
  // the file goes back to 42 KB.
  data: Data,
  descriptiveAIAlerts: DescriptiveAIAlerts,
  professionalMonitoring: ProfessionalMonitoring,

  // One calendar for every product's "event storage" row. It was four B2 keys
  // pointing at the same drawing in four brand colours
  // (`babyEventStorageIcon`, `petEventStorageIcon`, `trailEventStorageIcon`,
  // `birdCalenderIcon`) plus home's `Days30EventIcon`.
  eventStorage: EventStorage,
  // Open book. Trail calls the row "wildlife insights", bird calls it
  // "library"; the drawing is the same, so the name is the shape's job.
  library: Library,

  // The red X on every "what you lose by skipping" row. Pet, bird and trail
  // shipped byte-identical copies of this file and baby shipped a 48px PNG of
  // the same mark, so one entry replaces four assets. Rendered with
  // `monochrome={false}`: the red is a semantic "unavailable", not a brand
  // colour, and it is the same red on every product today.
  featureUnavailable: FeatureUnavailable,

  // The data-only upsell's three rows.
  liveView: LiveView,
  motionAlerts: MotionAlerts,
  snapshot: Snapshot,

  // The generic placeholder card drawn on the "Add new card" accordion —
  // not a real card-brand mark (visa/mastercard etc., which DO stay
  // partner-URL <img>s per the scope note above), so it's fair game to
  // recolour. Was a flat #03CB6A baked into the file; now `currentColor`
  // so it takes the page's primary colour like every other icon here.
  sampleCreditCard: SampleCreditCard,

  // The result-popup pair — see components/Icon/StatusIcon.tsx, which puts
  // one of these on a solid product/secondary-coloured disc. Together they
  // replace the whole family of bespoke per-scenario, per-partner CDN
  // illustrations (`FreeTrialActivatedIcon`, `paymentSuccessfulIcon`,
  // `paymentFailedIcon`, `DownGradeConfirmationIcon`, `attentionIcon`, …) —
  // every one of those drew "this worked" or "this didn't" as its own
  // picture; these two glyphs plus a colour say the same thing once.
  // Lucide's `circle-check-big`, unchanged: the ring stroke draws a visible
  // white inset ring against StatusIcon's coloured disc, not a redundant
  // outline — an earlier trim dropped it and read as a flat, ringless dot.
  success: Success,
  // Lucide's `triangle-alert`, unchanged — it has no built-in circle to
  // conflict with StatusIcon's disc.
  warning: Warning,

  // Same "glassStatus" disc treatment as the success/warning pair above,
  // used to replace the old full-width illustrations on MaxCameraCountReached
  // and SubscriptionUpgradeBannerV1 (both were bespoke per-partner CDN art
  // for a single static idea — "you've hit your limit" / "here's more").
  maxLimit: MaxLimitIcon,
  upgrade: UpgradeIcon,

  // The hub device drawn on the hub pairing screens (/addHubInfo). Duotone in
  // one ink: the outline is the brand ink, and the body fill — exported as a
  // flat #E3EDFB — is the same ink at 15% opacity, which is that exact tint
  // on white but follows the product colour and dark mode. Coordinates
  // rounded to two decimals (9.7 KB → 7.4 KB, no visible change at the sizes
  // it renders at).
  hub: Hub,

  // Plan feature list icons, keyed by the API's feature id rather than by a
  // call site. See featureIcons.ts.
  ...FEATURE_ICONS
} satisfies Record<string, TIconComponent>;

/**
 * Per-product art. Two kinds belong here:
 *
 *  - overrides of a shared slot — one name, different drawing per product.
 *    `eventStorage` and `subscriptionHeader` are the obvious cases: today they
 *    are six near-duplicate keys (`petEventStorageIcon`, `babyEventStorageIcon`,
 *    `birdCalenderIcon`, `petBuySubscriptionHeaderIcon`, ...) that collapse to
 *    one name the call site never branches on;
 *  - icons only one product has, e.g. `cryDetection` (baby).
 */
const PRODUCT_ICONS: Partial<
  Record<TProductName, Record<string, TIconComponent>>
> = {
  // `planListHeader` is one slot with five drawings — it replaces the four
  // near-duplicate B2 keys (`petBuySubscriptionHeaderIcon`,
  // `babyBuySubscriptionHeaderIcon`, `trailBuySubscriptionHeaderIcon`,
  // `birdGradientIcon`) that every plan screen used to branch on. Each
  // drawing lives at assets/icons/common/<product>PlanListHeader.svg —
  // one file per product, prefixed rather than folder-nested, resolved
  // per-product below the same way every other override in this map is.
  home: { planListHeader: HomeSecurityPlanListHeader },
  bird: {
    planListHeader: BirdPlanListHeader,
    birdDetected: BirdDetected,
    wiki: Wiki
  },
  pet: {
    planListHeader: PetPlanListHeader,
    multiPetRecognition: MultiPetRecognition,
    // Not in the Figma icon set. Lifted out of the old B2 `trendIcon.svg` by
    // dropping its baked-in #F6F6F6 disc and scaling the glyph from the 16.7
    // units it filled in that 40px disc to the 20 its neighbours span in a 24
    // box, so it carries the same weight. Replace if design ships one.
    activityInsights: ActivityInsights
  },
  baby: {
    planListHeader: BabyPlanListHeader,
    cryDetection: BabyCryDetection,
    tempHumidityAlert: BabyTempHumidity
  },
  trail: {
    planListHeader: TrailPlanListHeader,
    wildlifeIdentification: WildlifeIdentification
  }
};

/**
 * Widens to `string` while the maps are empty. Once COMMON_ICONS has entries,
 * narrow this to `keyof typeof COMMON_ICONS` (plus each product map's keys) so
 * a typo in a name becomes a compile error rather than a blank space.
 */
export type TIconName = string;

/**
 * Resolves an icon for a product: the product's own art if it has any under
 * that name, otherwise the common icon. Undefined when neither has it, which
 * `<Icon>` renders as nothing.
 */
export const resolveIcon = (
  name: TIconName,
  product: TProductName
): TIconComponent | undefined =>
  PRODUCT_ICONS[product]?.[name] ??
  (COMMON_ICONS as Record<string, TIconComponent>)[name];

export const ICON_NAMES = Object.keys(COMMON_ICONS) as TIconName[];
